import type { BillingAccount } from '~/gql/billing/billing'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import BillingPortalButton from '~/pages/backoffice/admin/billing/BillingPortalButton'

type Props = {
  account: Pick<BillingAccount, 'tier' | 'paymentTerm' | 'currentPeriodEnd' | 'cancelAtPeriodEnd' | 'cancelAt' | 'scheduledTier' | 'scheduledPaymentTerm' | 'scheduledChangeAt'>
}

const LONG_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }

/**
 * Le palier payé, à la place des offres une fois abonné (#352), avec ce qui va
 * changer à l'échéance : renouvellement, descente programmée ou résiliation
 * (#353). Tout se règle dans le portail client Stripe.
 */
function SubscriptionSummary({ account }: Props) {
  const { t, i18n } = useTranslation()
  const date = (value?: Date | null) => formatSessionDate(value, i18n.language, null, LONG_DATE)
  // Résilié à l'échéance, ou à une date posée depuis le dashboard Stripe.
  const endsAt = account.cancelAt ?? (account.cancelAtPeriodEnd ? account.currentPeriodEnd : null)
  const plan = (tier: string, term: string) => t('billing.subscription.current', { tier: t(`billing.tier.${tier}`), term: t(`billing.term.${term}`) })

  return (
    <Card {...tourAnchor('billing.plan')}>
      <CardHeader>
        <CardTitle>{t('billing.plan.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="font-medium">{plan(account.tier, account.paymentTerm)}</p>
          {endsAt
            ? <p className="text-sm">{t('billing.subscription.cancels', { date: date(endsAt) })}</p>
            : account.currentPeriodEnd && <p className="text-sm text-muted-foreground">{t('billing.subscription.renews', { date: date(account.currentPeriodEnd) })}</p>}
          {!endsAt && account.scheduledTier && account.scheduledPaymentTerm && (
            <p className="text-sm">
              {t('billing.subscription.scheduled', {
                tier: t(`billing.tier.${account.scheduledTier}`),
                term: t(`billing.term.${account.scheduledPaymentTerm}`).toLocaleLowerCase(i18n.language),
                date: date(account.scheduledChangeAt),
              })}
            </p>
          )}
        </div>
        <div {...tourAnchor('billing.manage')}>
          <BillingPortalButton>{t('billing.portal.manage')}</BillingPortalButton>
        </div>
      </CardContent>
    </Card>
  )
}

export default SubscriptionSummary
